'use client';

import React, { useCallback, useMemo, useState } from 'react';

import { debounce } from 'lodash';
import Image from 'next/image';

import minusIcon from '@/assets/icons/minus-small-icon-cart.svg';
import plusIcon from '@/assets/icons/plus-small-icon.svg';
import cn from '@/helpers/utils/classNames';

interface IQuantityProps {
  value?: number;
  limit?: number;
  minValue?: number;
  onChange?: (value: number) => void;
  className?: string;
  readonly?: boolean;
  withDebouncing?: boolean;
  disableDebounce?: boolean;
  inputClassName?: string;
  disabled?: boolean;
}

const Quantity = ({
  onChange,
  value = 0,
  className,
  limit,
  minValue = 0,
  readonly = true,
  withDebouncing = false,
  inputClassName,
  disabled = false,
  disableDebounce = false,
}: IQuantityProps) => {
  const [count, setCount] = useState<any>(value);

  const handleIncreaseQuantity = () => {
    const updatedQuantity = +count + 1;

    if (limit && updatedQuantity <= limit) {
      setCount(() => updatedQuantity);
      if (disableDebounce) {
        handleUpdateQuantity(updatedQuantity, count as number, value);
        return;
      }
      debouncedUpdate(updatedQuantity, count as number, value);
    }
  };

  const handleDecreaseQuantity = () => {
    const updatedQuantity = +count - 1;

    if (minValue && updatedQuantity >= minValue) {
      setCount(() => updatedQuantity);
      if (disableDebounce) {
        handleUpdateQuantity(updatedQuantity, count as number, value);
        return;
      }
      debouncedUpdate(updatedQuantity, count as number, value);
    }
  };

  const handleUpdateQuantity = useCallback(
    (updatedQuantity: number, currentStateCount: number, currentCartCount: number) => {
      const isQuantityInRange = updatedQuantity && +updatedQuantity >= 1 && +updatedQuantity <= 25;

      if (isQuantityInRange && onChange) {
        onChange(updatedQuantity);
      } else if (+updatedQuantity > 25 && currentStateCount !== currentCartCount && onChange) {
        onChange(currentStateCount);
      }
    },
    [onChange]
  );

  const debouncedUpdate = useMemo(() => debounce(handleUpdateQuantity, 1000), [handleUpdateQuantity]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value.trim() === '' ? '' : parseInt(e.target.value, 10);
    const isQuantityInRange = newValue && +newValue >= 1 && +newValue <= 25;
    const hasEmptyValue = !newValue && newValue !== 0;

    if (hasEmptyValue || isQuantityInRange) {
      setCount(newValue);
    }

    if (disableDebounce) {
      handleUpdateQuantity(newValue as number, count as number, value);
      return;
    }

    debouncedUpdate(newValue as number, count as number, value);
  };

  const handleBlur = () => {
    if (!count && count !== 0) setCount(value);
  };

  const handlerKey = (event: any) => {
    if (event.key === 'e' || event.key === 'E' || event.key === '-' || event.key === '.' || event.key === '+') {
      event.preventDefault();
    }
  };

  return (
    <div
      className={cn(
        'v2-rounded-xs focus-within:v2-shadow-round v2-flex v2-min-w-[115px] v2-items-center v2-justify-between v2-border v2-border-gray-300 v2-bg-white v2-py-[10px] v2-px-4',
        className,
        { 'pointer-events-none': disabled }
      )}
    >
      <Image
        src={minusIcon}
        alt="quantity-minus-icon"
        role="button"
        width={20}
        onClick={handleDecreaseQuantity}
        className={cn({ '!v2-cursor-not-allowed  v2-opacity-25': count <= minValue })}
      />
      <input
        type="number"
        min={minValue}
        className={cn(
          'v2-font-sweet-sans-pro input-number-remove-btns v2-w-10 v2-cursor-default v2-border-none v2-text-center v2-text-base v2-font-medium v2-text-gray-700 focus-within:v2-border-0 focus-within:v2-outline-none focus-within:v2-ring-0',
          inputClassName
        )}
        value={count}
        readOnly={readonly}
        max={limit || ''}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handlerKey}
      />
      <Image
        src={plusIcon}
        alt="quantity-plus-icon"
        className={cn({ '!v2-cursor-not-allowed': count >= (limit ?? 25) })}
        role="button"
        width={20}
        onClick={handleIncreaseQuantity}
      />
    </div>
  );
};

export default Quantity;
